/**
 * API utilities with automatic timezone handling and enhanced security
 */
import { getUserTimezone } from './timezone';
import { TokenManager, SecurityUtils } from './auth';

/**
 * Enhanced fetch function that automatically includes timezone headers and authentication
 */
export const fetchWithTimezone = async (url, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'X-Timezone': getUserTimezone(),
    'X-Requested-With': 'XMLHttpRequest', // Security header
  };

  // Get auth token if available
  const token = TokenManager.getAccessToken();
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Sanitize request body if present
  let body = options.body;
  if (body && typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const sanitized = sanitizeRequestData(parsed);
      body = JSON.stringify(sanitized);
    } catch (error) {
      // If not JSON, leave as is
    }
  }

  const enhancedOptions = {
    ...options,
    body,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, enhancedOptions);
    
    // Handle authentication errors
    if (response.status === 401) {
      // Token might be expired, let the auth service handle refresh
      TokenManager.clearTokens();
      window.dispatchEvent(new CustomEvent('authError', { detail: 'unauthorized' }));
    }
    
    return response;
  } catch (error) {
    // Handle network errors
    throw new Error('Network error. Please check your connection.');
  }
};

/**
 * Sanitize request data to prevent XSS and other security issues
 */
function sanitizeRequestData(data) {
  if (Array.isArray(data)) {
    return data.map(item => sanitizeRequestData(item));
  }
  
  if (data && typeof data === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        sanitized[key] = SecurityUtils.sanitizeInput(value);
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeRequestData(value);
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }
  
  return data;
}

/**
 * Enhanced API client with timezone support, authentication, and security
 */
export const apiClient = {
  async get(url, options = {}) {
    const response = await fetchWithTimezone(url, { method: 'GET', ...options });
    return this.handleResponse(response);
  },
  
  async post(url, data, options = {}) {
    const response = await fetchWithTimezone(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
    return this.handleResponse(response);
  },
  
  async put(url, data, options = {}) {
    const response = await fetchWithTimezone(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
    return this.handleResponse(response);
  },
  
  async patch(url, data, options = {}) {
    const response = await fetchWithTimezone(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
    return this.handleResponse(response);
  },
  
  async delete(url, options = {}) {
    const response = await fetchWithTimezone(url, { method: 'DELETE', ...options });
    return this.handleResponse(response);
  },

  /**
   * Handle API response with error checking
   */
  async handleResponse(response) {
    if (!response.ok) {
      let errorData = null;
      let errorMessage = 'An error occurred';

      try {
        errorData = await response.json();
        errorMessage = (
          errorData.detail ||
          errorData.error ||
          errorMessage
        );
      } catch (e) {
        errorMessage = response.statusText || errorMessage;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.response = response;
      error.data = errorData;
      throw error;
    }

    return response;
  },

  /**
   * Upload file with progress tracking
   */
  async uploadFile(url, file, onProgress = null, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional form data if provided
    if (options.additionalData) {
      Object.entries(options.additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const token = TokenManager.getAccessToken();
    const headers = {
      'X-Requested-With': 'XMLHttpRequest',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed: Network error'));
      });
      
      xhr.open('POST', url);
      
      // Set headers
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      
      xhr.send(formData);
    });
  }
};

/**
 * Axios interceptor setup for timezone headers (if using axios)
 */
export const setupAxiosTimezone = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      config.headers['X-Timezone'] = getUserTimezone();
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  return axiosInstance;
};